import { useTranslation } from "react-i18next";
import { useEffect, useState } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import DocumentDetailPanel from '@/components/documents/DocumentDetailPanel';

export default function DocumentsHubPage({ user, onLogout }) {
  const { t } = useTranslation();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [docs, setDocs] = useState([]);
  const [filters, setFilters] = useState({ type: 'all', status: 'current' });
  const [selectedDocId, setSelectedDocId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const p = await axios.get('/projects');
        setProjects(p.data);
        if (p.data.length) setSelectedProject(p.data[0].id);
      } catch {}
    })();
  }, []);

  useEffect(() => { if (selectedProject) loadDocs(); }, [selectedProject, filters]);

  const loadDocs = async () => {
    try {
      const params = new URLSearchParams();
      params.append('project_id', selectedProject);
      if (filters.type && filters.type !== 'all') params.append('doc_type', filters.type);
      if (filters.status !== 'all') params.append('include_superseded', (filters.status !== 'current').toString());
      const res = await axios.get(`/cycle3/documents?${params.toString()}`);
      const list = (res.data || []).filter(d => filters.status === 'all' || (filters.status === 'current' ? !d.superseded : d.superseded));
      setDocs(list);
    } catch (e) { toast.error(t("documents.failedToLoad")); }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-4">
        <h1 className="text-2xl font-semibold">{t("documents.title")}</h1>
        <div className="flex gap-3 items-center">
          <Label>{t("documents.project")}</Label>
          <Select value={selectedProject} onValueChange={setSelectedProject}>
            <SelectTrigger className="w-64"><SelectValue /></SelectTrigger>
            <SelectContent>
              {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Label>{t("documents.type")}</Label>
          <Select value={filters.type} onValueChange={(v) => setFilters(f => ({ ...f, type: v }))}>
            <SelectTrigger className="w-48"><SelectValue placeholder={t("documents.all")}/></SelectTrigger>
            <SelectContent>
              {['all', 'CONTRACT','OFFER','DRAWING','PERMIT','PHOTOS','PO','GRN','INVOICE','OTHER'].map(typ => (
                <SelectItem key={typ} value={typ}>{typ === 'all' ? t("documents.all") : typ}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Label>{t("documents.status")}</Label>
          <Select value={filters.status} onValueChange={(v) => setFilters(f => ({ ...f, status: v }))}>
            <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="current">{t("documents.current")}</SelectItem>
              <SelectItem value="superseded">{t("documents.superseded")}</SelectItem>
              <SelectItem value="all">{t("documents.all")}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-1 gap-3">
          {docs.map(d => (
            <Card key={d.id} onClick={() => setSelectedDocId(d.id)} className="cursor-pointer">
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <div className="font-medium">{d.original_filename} <span className="text-[11px] text-slate-500">v{d.version || 1}</span></div>
                  <div className="text-xs text-sm text-slate-500">{d.doc_type} • {d.entity_type || 'project'} • {new Date(d.uploaded_at).toLocaleString()}</div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={(e) => { e.stopPropagation(); window.open(`/api/cycle3/documents/${d.id}/download`, '_blank'); }}>{t("documents.open")}</Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
      {selectedDocId && (
        <DocumentDetailPanel docId={selectedDocId} user={user} onClose={() => setSelectedDocId(null)} onUpdated={loadDocs} />
      )}
    </Layout>
  );
}
